// Keeps a person mask from flickering at its edge without making it lag.
//
// The segmenter's confidence at a hair edge wavers a little from frame to
// frame, and the cutoff sits right through that band, so edge pixels flip
// between person and background while the person sits still (Adam, 2026-10-13:
// "edges of my hair keep flickering in and out of blur"). Blending every frame
// with the last one stops that but makes the mask trail a moving person, which
// he has already ruled out (2026-10-04: "always playing catch up to movement").
//
// So the blend depends on how much the mask is moving. Held still, few pixels
// change by much and each moves only part of the way to its new value; moving,
// many pixels change at once and the new mask is taken whole.
//
// And only the way out is eased: a pixel becoming person takes its new value at
// once. Easing both ways put the blur on his face whenever he talked or turned
// a little, too little to count as moving, because the newly covered cheek or
// jaw took several frames to come sharp (2026-10-14: "blur often extends too
// far onto my face"; the blur lab, scripts/blur-lab.mjs, showed the leading
// edge going soft). A flickering hair edge now rests near sharp instead of
// near blurred, and a head that moves leaves a sharp sliver behind it for a
// few frames rather than blurring its front edge.

/** A pixel counts as changed when its alpha moved by more than this. */
const CHANGED = 0.4;
/** Share of the person's area that changed, below which the mask is held
 * still (calm) and above which it is moving. */
const CALM_BELOW = 0.02;
const MOVING_ABOVE = 0.06;
/** How far a pixel moves toward its new value each frame while still. */
const CALM_STEP = 0.2;

/** How much of the way to the new mask this frame goes, 0 to 1. */
export function settleStep(changedShare: number): number {
  if (changedShare <= CALM_BELOW) return CALM_STEP;
  if (changedShare >= MOVING_ABOVE) return 1;
  return CALM_STEP + ((1 - CALM_STEP) * (changedShare - CALM_BELOW)) / (MOVING_ABOVE - CALM_BELOW);
}

/** Moves `prev` toward `next` in place and returns the step it used. `prev`
 * becomes the settled mask; `next` is left as it was. Both are alphas 0 to 1
 * and must be the same length; the caller starts `prev` as a copy of the first
 * mask and again whenever the size changes. `mode` "both" eases a pixel in as
 * well as out, the behavior before 2026-10-14, kept for the blur lab. */
export function settleMask(prev: Float32Array, next: Float32Array, mode: "rise" | "both" = "rise"): number {
  const riseNow = mode === "rise";
  let changed = 0;
  let area = 0;
  for (let i = 0; i < next.length; i++) {
    const n = next[i]!;
    if (n > 0.5) area++;
    if (Math.abs(n - prev[i]!) > CHANGED) changed++;
  }
  const step = settleStep(changed / Math.max(1, area));
  for (let i = 0; i < next.length; i++) {
    const n = next[i]!;
    const p = prev[i]!;
    prev[i] = riseNow && n > p ? n : p + (n - p) * step;
  }
  return step;
}

/** An exponential average of a value that jitters from frame to frame, started
 * at its first reading. */
export function ease(prev: number | null, next: number, rate: number): number {
  return prev === null ? next : prev + (next - prev) * rate;
}
